import { api } from './api';

export interface CustomerAuthRequest {
  ClientId: string;
  ClientSecret: string;
}

export interface SpaTenantAuthRequest {
  Host?: string;
  TenantSlug?: string;
  Path?: string;
}

export interface UserAuthRequest {
  Email: string;
  Password: string;
  CustomerId: string;
}

export interface HybridAuthRequest {
  CustomerToken: string;
  UserToken: string;
}

export interface ValidateTokenRequest {
  Token: string;
}

export interface CustomerTokenResponse {
  token: string;
  customerId: string;
  customerName: string;
  isActive: boolean;
  expiresAt: string;
}

export interface UserTokenResponse {
  token: string;
  userId: string;
  userName: string;
  email: string;
  role: string;
  expiresAt: string;
  refreshToken?: string;
  expiresIn?: number;
}

export interface HybridTokenResponse {
  token: string;
  refreshToken?: string;
  customerId: string;
  userId: string;
  customerName: string;
  userName: string;
  email: string;
  role: string;
  expiresAt: string;
  expiresIn?: number;
}

export interface ValidateTokenResponse {
  customerId: string;
  userId: string;
  customerName: string;
  userName: string;
  email: string;
  role: string;
  isValid: boolean;
}

class HybridAuthService {
  private baseUrl = '/auth';

  async authenticateCustomer(request: CustomerAuthRequest): Promise<CustomerTokenResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/customer`, request);
      return response.data;
    } catch (error: any) {
      console.error('Erro na autenticação do customer:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Erro na autenticação do customer');
    }
  }

  async authenticateSpaTenant(request: SpaTenantAuthRequest): Promise<CustomerTokenResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/customer/spa`, request);
      return response.data;
    } catch (error: any) {
      console.error('Erro na autenticação SPA do tenant:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Erro na autenticação do tenant');
    }
  }

  async authenticateUser(request: UserAuthRequest): Promise<UserTokenResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/user`, request);
      return response.data;
    } catch (error: any) {
      console.error('Erro na autenticação do usuário:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Erro na autenticação do usuário');
    }
  }

  async createHybridToken(request: HybridAuthRequest): Promise<HybridTokenResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/hybrid`, request);
      return response.data;
    } catch (error: any) {
      console.error('Erro na criação do token híbrido:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Erro na criação do token híbrido');
    }
  }

  async validateToken(request: ValidateTokenRequest): Promise<ValidateTokenResponse> {
    try {
      const response = await api.post(`${this.baseUrl}/validate`, request);
      return response.data;
    } catch (error: any) {
      console.error('Erro na validação do token:', error);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Erro na validação do token');
    }
  }

  async isTokenValid(token: string): Promise<boolean> {
    try {
      const response = await this.validateToken({ Token: token });
      return response.isValid;
    } catch (error) {
      return false;
    }
  }

  async refreshHybridToken(customerToken: string, userToken: string): Promise<HybridTokenResponse> {
    try {
      await this.validateToken({ Token: customerToken });
      await this.validateToken({ Token: userToken });

      return await this.createHybridToken({
        CustomerToken: customerToken,
        UserToken: userToken
      });
    } catch (error: any) {
      console.error('Erro na renovação do token híbrido:', error);
      throw new Error('Erro na renovação do token híbrido');
    }
  }
}

export const hybridAuthService = new HybridAuthService();
